import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { Post } from '../types/post';
import { PostCard } from '../components/post';
import { LoadingIndicator, PostCardSkeleton, ActionSheet, ActionSheetOption } from '../components/common';
import * as blueskyService from '../services/bluesky';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type ListFeedRouteProp = RouteProp<RootStackParamList, 'ListFeed'>;

export const ListFeedScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<ListFeedRouteProp>();
  const { listUri, listName } = route.params;
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repostMenuVisible, setRepostMenuVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    loadFeed();
  }, [listUri]);

  const loadFeed = async (refresh = false) => {
    if (isLoading || isLoadingMore) return;

    try {
      if (refresh) {
        setIsRefreshing(true);
      } else if (posts.length === 0) {
        setIsLoading(true);
      }
      setError(null);

      const data = await blueskyService.getListFeed(listUri, refresh ? undefined : cursor);
      
      if (refresh) {
        setPosts(data.feed);
      } else {
        setPosts(prev => [...prev, ...data.feed]);
      }
      
      setCursor(data.cursor);
    } catch (err) {
      console.error('List feed load error:', err);
      setError('フィードの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setIsLoadingMore(false);
    }
  };

  const onRefresh = useCallback(() => {
    loadFeed(true);
  }, [listUri]);

  const loadMore = useCallback(() => {
    if (!cursor || isLoadingMore || isRefreshing) return;
    
    setIsLoadingMore(true);
    loadFeed();
  }, [cursor, isLoadingMore, isRefreshing, listUri]);

  const handleLike = useCallback(async (post: Post) => {
    try {
      if (post.viewer?.like) {
        await blueskyService.unlikePost(post.viewer.like);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  likeCount: p.likeCount - 1,
                  viewer: { ...p.viewer, like: undefined },
                }
              : p
          )
        );
      } else {
        const likeUri = await blueskyService.likePost(post.uri, post.cid);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  likeCount: p.likeCount + 1,
                  viewer: { ...p.viewer, like: likeUri },
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  }, []);

  const handleRepost = useCallback((post: Post) => {
    setSelectedPost(post);
    setRepostMenuVisible(true);
  }, []);

  const executeRepost = useCallback(async (post: Post) => {
    try {
      if (post.viewer?.repost) {
        await blueskyService.unrepost(post.viewer.repost);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  repostCount: p.repostCount - 1,
                  viewer: { ...p.viewer, repost: undefined },
                }
              : p
          )
        );
      } else {
        const repostUri = await blueskyService.repost(post.uri, post.cid);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  repostCount: p.repostCount + 1,
                  viewer: { ...p.viewer, repost: repostUri },
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error('Repost error:', err);
    }
  }, []);

  const handleQuotePost = useCallback((post: Post) => {
    navigation.navigate('Compose', {
      quoteTo: {
        uri: post.uri,
        cid: post.cid,
      },
    });
  }, [navigation]);

  const repostMenuOptions: ActionSheetOption[] = selectedPost
    ? [
        {
          label: selectedPost.viewer?.repost ? 'リポストを取り消す' : 'リポスト',
          icon: 'repeat-outline',
          onPress: () => {
            if (selectedPost) {
              executeRepost(selectedPost);
            }
          },
        },
        {
          label: '引用',
          icon: 'chatbox-outline',
          onPress: () => {
            if (selectedPost) {
              handleQuotePost(selectedPost);
            }
          },
        },
      ]
    : [];

  const handleBookmark = useCallback(async (post: Post) => {
    try {
      if (post.viewer?.bookmark) {
        await blueskyService.unbookmarkPost(post.viewer.bookmark);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  viewer: { ...p.viewer, bookmark: undefined },
                }
              : p
          )
        );
      } else {
        const bookmarkUri = await blueskyService.bookmarkPost(post.uri, post.cid);
        setPosts(prev =>
          prev.map(p =>
            p.uri === post.uri
              ? {
                  ...p,
                  viewer: { ...p.viewer, bookmark: bookmarkUri },
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error('Bookmark error:', err);
    }
  }, []);

  const renderFooter = () => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footer}>
        <LoadingIndicator size="small" />
      </View>
    );
  };

  if (error && posts.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{listName}</Text>
        </View>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (isLoading && posts.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{listName}</Text>
        </View>
        {[...Array(5)].map((_, index) => (
          <PostCardSkeleton key={index} />
        ))}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{listName}</Text>
      </View>
      
      <FlatList
        data={posts}
        keyExtractor={item => item.uri}
        renderItem={({ item }) => (
          <PostCard
            post={item}
            onPress={() => navigation.navigate('PostDetail', { uri: item.uri })}
            onLike={() => handleLike(item)}
            onRepost={() => handleRepost(item)}
            onBookmark={() => handleBookmark(item)}
            onReply={() => console.log('Reply to:', item.uri)}
            onAuthorPress={() => navigation.navigate('Profile', { did: item.author.did })}
          />
        )}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={10}
        removeClippedSubviews={true}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        contentContainerStyle={posts.length === 0 ? styles.emptyContainer : undefined}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>投稿がありません</Text>
          </View>
        }
      />

      {/* リポストメニュー */}
      <ActionSheet
        visible={repostMenuVisible}
        onClose={() => setRepostMenuVisible(false)}
        options={repostMenuOptions}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  headerTitle: {
    ...typography.h3,
    color: colors.text,
  },
  footer: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    ...typography.body,
    color: colors.error,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing['3xl'],
  },
  emptyText: {
    ...typography.body,
    color: colors.textSecondary,
  },
});
